import { store } from "@store/index";
import {
  type SideNavigationV2Props,
  type PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import {
  updateTriggerVersion,
  getTrigger,
  upsertTriggerEnvVariablesAsync,
  deleteTriggerEventSubscription,
  toggleTriggerEventSubscription,
  LATEST_VERSION_VALUE,
  type Trigger,
  type TriggerEventSubscription,
} from "@store/features/settings/workflowSlice";
import { open as openDialog } from "@store/features/layout/dialogSlice";
import { iconTypes } from "skedyul-ui/ui";
import dayjs from "dayjs";
import { trpc } from "@utils/api";
import { toast } from "sonner";
import type {
  TriggerSubscriptionRow,
  TriggerMatchConfig,
  CrmModelOption,
  InstalledAppOption,
} from "skedyul-ui/src/components/ui/TriggerSentence";
import {
  buildTriggerCatalogKey,
  flattenEventContextToFieldOptions,
} from "skedyul-ui/src/components/ui/TriggerSentence/utils";
import {
  appEventInputTypeMatchesEventType,
  constrainCrmModelsByEventInputs,
  findAppEventWorkflowInputs,
  findCrmEventWorkflowInputs,
} from "skedyul-core/features/events";

type TriggerWithEventSubscriptions = Partial<Trigger>;

export const editTriggerViewSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }>,
): SideNavigationV2Props<PageV2Component> => {
  const {
    workflows: {
      workflow: { data: workflowData, isLoading },
      workflows: { data: workflowsList },
    },
    sideNavigation: {
      right: { isOpen },
    },
  } = args.state;

  // Prefer an explicitly loaded trigger; otherwise, fall back to first associated trigger (best-effort)
  const explicitTrigger = workflowData?.trigger ?? null;
  const fallbackTrigger = workflowData
    ? (workflowData as unknown as { triggers?: Array<TriggerWithEventSubscriptions> })
        ?.triggers?.[0] ?? null
    : null;
  const trigger: TriggerWithEventSubscriptions | null = explicitTrigger ?? fallbackTrigger;

  // App-bundled workflows only accept that app's events (YAML @app/... inputs)
  const workflowAppHandle =
    (
      workflowData as
        | { app?: { handle?: string | null } | null }
        | null
        | undefined
    )?.app?.handle ?? null;
  const isAppWorkflow = Boolean(workflowAppHandle);

  // Transform event subscriptions to TriggerSubscriptionRow format
  const triggerSubscriptions: TriggerSubscriptionRow[] = (
    trigger?.eventSubscriptions ?? []
  ).map((sub: TriggerEventSubscription) => ({
    id: sub.id,
    matchConfig: (sub as unknown as { matchConfig?: TriggerMatchConfig }).matchConfig ?? null,
    eventType: sub.eventType,
    condition: sub.condition,
    isEnabled: sub.isEnabled,
    inputMappings: (sub.inputMappings as Record<string, string>) ?? {},
  }));

  const workflowId = trigger?.workflowId || workflowData?.id || "";

  const followLatest = Boolean(
    (trigger as { followLatest?: boolean } | null)?.followLatest,
  );
  const selectedVersionId = trigger?.workflowVersionId || "";
  const isDeactivated = !followLatest && !selectedVersionId;
  const selectedSelectValue = followLatest
    ? LATEST_VERSION_VALUE
    : isDeactivated
      ? "__none__"
      : selectedVersionId;
  // Reload TriggerSentence catalog when Live Version changes so CRM/app
  // availableOperations track the resolved version's YAML inputs (not the
  // editor's open version).
  const catalogKey = buildTriggerCatalogKey({
    followLatest,
    workflowVersionId: selectedVersionId || null,
  });

  type VersionOptionSource = {
    id: string;
    version?: number | null;
    versionLabel?: string | null;
    createdAt?: string | Date;
    workflowId?: string;
  };

  const listWorkflow = workflowId
    ? workflowsList.find((w) => w.id === workflowId)
    : undefined;
  const detailVersions = (workflowData?.versions || []) as VersionOptionSource[];
  const listVersions = (listWorkflow?.versions || []) as VersionOptionSource[];
  const versionSources =
    detailVersions.length > 0 ? detailVersions : listVersions;

  const versionItems = versionSources.map((v) => ({
    value: v.id,
    label: v.versionLabel || (v.version != null ? `v${v.version}` : v.id.slice(-6)),
    icon: "Package" as iconTypes,
    suffix: v.createdAt
      ? `Created ${dayjs(v.createdAt).fromNow()}`
      : undefined,
  }));

  return {
    side: "right",
    isOpen,
    width: "700px",
    component: {
      component: "PageV2",
      props: {
        hideTitle: true,
        content: {
          structure: "sidebar",
          components: [
            {
              type: "Card",
              title: "Configuration",
              props: {
                header: {
                  title: "Configuration",
                  description: "Adjust settings for how this runs",
                },
                form: {
                  formVersion: "v1",
                  id: "configuration-form",
                  fields: [
                    {
                      component: "Select",
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        id: "workflowVersionId",
                        label: "Live Version",
                        placeholder: isDeactivated
                          ? "Deactivated"
                          : followLatest
                            ? "Latest"
                            : "Select a version",
                        value: selectedSelectValue,
                        items: [
                          {
                            value: "__none__",
                            label: "Deactivate",
                            icon: "WebhookOff" as iconTypes,
                          },
                          {
                            value: LATEST_VERSION_VALUE,
                            label: "Latest",
                            icon: "Sparkles" as iconTypes,
                            suffix: "Always use newest published version",
                          },
                          ...versionItems,
                        ],
                        handleChange: (value: string) => {
                          void store.dispatch(
                            updateTriggerVersion({
                              id: trigger?.id || "",
                              workflowVersionId:
                                value === "__none__" ? null : value,
                            }),
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Triggers",
              props: {
                header: {
                  title: "Triggers",
                  description:
                    "Workplace triggers run whenever the event matches. Thread recipes run only on threads this workflow is in.",
                },
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "TriggerSentenceList",
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        subscriptions: triggerSubscriptions,
                        isLoading,
                        // Allow adding the first trigger even when the Trigger
                        // row is missing / still a "new" placeholder — Configure
                        // will ensure a real trigger exists before create.
                        disabled: !workflowId,
                        catalogKey,
                        menuProps: {
                          crmModels: [],
                          installedApps: [],
                          showThread: !isAppWorkflow,
                          // Catalog populate decides visibility: empty crmModels
                          // hides CRM. App workflows with @crm YAML inputs get models.
                          showCrm: true,
                        },
                        loadCatalog: async () => {
                          const [models, apps] = await Promise.all([
                            trpc.crm.getModels.query(),
                            trpc.apps.listInstalledAppEvents.query(),
                          ]);

                          const allCrmModels: CrmModelOption[] = (
                            models as Array<{
                              id: string;
                              handle: string;
                              name: string;
                              namePlural?: string;
                              icon?: string;
                            }>
                          ).map((m) => ({
                            id: m.id,
                            handle: m.handle,
                            name: m.name,
                            namePlural: m.namePlural ?? `${m.name}s`,
                            icon:
                              (m.icon as CrmModelOption["icon"]) ??
                              "Database",
                          }));

                          // App-bundled workflows hide CRM until YAML @crm inputs exist.
                          let crmModels: CrmModelOption[] = isAppWorkflow
                            ? []
                            : allCrmModels;

                          let installedApps: InstalledAppOption[] = (
                            apps as Array<{
                              handle: string;
                              name: string;
                              iconUrl: string | null;
                              events: Array<{
                                name: string;
                                label: string;
                                description?: string;
                                icon?: string;
                              }>;
                            }>
                          ).map((app) => ({
                            handle: app.handle,
                            name: app.name,
                            iconUrl: app.iconUrl,
                            events: app.events.map((e) => ({
                              name: e.name,
                              label: e.label,
                              description: e.description,
                              icon: e.icon as InstalledAppOption["events"][number]["icon"],
                            })),
                          }));

                          // Filter catalog by YAML @app/... / @crm/... inputs when available.
                          // Prefer triggerId (respects pinned version); fall back to workflowId
                          // so Add Trigger is constrained before ensureTrigger runs.
                          if (
                            (trigger?.id && trigger.id !== "new") ||
                            workflowId
                          ) {
                            try {
                              const { inputs } =
                                await trpc.eventSubscriptions.getTriggerWorkflowInputs.query(
                                  trigger?.id && trigger.id !== "new"
                                    ? { triggerId: trigger.id }
                                    : { workflowId },
                                );
                              const appEventInputs =
                                findAppEventWorkflowInputs(inputs);
                              const crmEventInputs =
                                findCrmEventWorkflowInputs(inputs);

                              // App workflows: only this app, and only events matching YAML inputs
                              if (isAppWorkflow && workflowAppHandle) {
                                installedApps = installedApps.filter(
                                  (app) => app.handle === workflowAppHandle,
                                );

                                if (appEventInputs.length > 0) {
                                  installedApps = installedApps.map((app) => ({
                                    ...app,
                                    events: app.events.filter((event) => {
                                      const eventType = `app.${app.handle}.${event.name}`;
                                      return appEventInputs.some((inp) =>
                                        appEventInputTypeMatchesEventType(
                                          inp.type,
                                          eventType,
                                        ),
                                      );
                                    }),
                                  }));
                                }
                              }

                              if (crmEventInputs.length > 0) {
                                crmModels = constrainCrmModelsByEventInputs(
                                  allCrmModels,
                                  crmEventInputs,
                                );
                              }
                            } catch (error) {
                              console.error(
                                "Failed to filter trigger catalog by workflow inputs",
                                error,
                              );
                            }
                          } else if (isAppWorkflow && workflowAppHandle) {
                            installedApps = installedApps.filter(
                              (app) => app.handle === workflowAppHandle,
                            );
                          }

                          return { crmModels, installedApps };
                        },
                        loadThreadEventFields: async (eventType: string) => {
                          const context =
                            await trpc.eventSubscriptions.getEventContext.query({
                              eventType,
                            });
                          return flattenEventContextToFieldOptions(
                            context.fields ?? [],
                          );
                        },
                        loadCrmFields: async (modelHandle: string) => {
                          const models = (await trpc.crm.getModels.query()) as Array<{
                            id: string;
                            handle: string;
                          }>;
                          const model = models.find((m) => m.handle === modelHandle);
                          if (!model) return [];

                          const fields = (await trpc.crm.getFields.query({
                            modelId: model.id,
                          })) as Array<{
                            handle: string;
                            label?: string | null;
                            type?: string | null;
                            definition?: {
                              type?: string | null;
                              options?: Array<{
                                value: string;
                                label: string;
                              }> | null;
                            } | null;
                          }>;

                          return fields.map((f) => {
                            const options = f.definition?.options ?? [];
                            const type =
                              f.type ?? f.definition?.type ?? "STRING";
                            return {
                              handle: f.handle,
                              label: f.label || f.handle,
                              type,
                              hasOptions: options.length > 0,
                              options: options.map((o) => ({
                                value: o.value,
                                label: o.label,
                              })),
                            };
                          });
                        },
                        // New drafts use Configure → mapping dialog → create
                        onConfigure: (config: TriggerMatchConfig) => {
                          void (async () => {
                            if (!workflowId) {
                              toast.error("Please save the workflow first");
                              return;
                            }

                            let triggerId =
                              trigger?.id && trigger.id !== "new"
                                ? trigger.id
                                : null;

                            if (!triggerId) {
                              try {
                                const ensured =
                                  await trpc.workflows.ensureTrigger.mutate({
                                    workflowId,
                                  });
                                triggerId = ensured.id;
                                await store.dispatch(
                                  getTrigger({ id: triggerId }),
                                );
                              } catch (error) {
                                console.error(
                                  "Failed to create workflow trigger:",
                                  error,
                                );
                                toast.error(
                                  "Failed to prepare workflow for triggers",
                                );
                                return;
                              }
                            }

                            store.dispatch(
                              openDialog({
                                schema: "EDIT_TRIGGER_INPUT_MAPPING",
                                params: {
                                  mode: "create",
                                  triggerId,
                                  eventType: config.eventType,
                                  matchConfig: config,
                                  inputMappingsMap: {},
                                },
                              }),
                            );
                          })();
                        },
                        onMatchConfigChange: async (
                          id: string,
                          config: TriggerMatchConfig | null | undefined,
                        ) => {
                          if (!config) return;
                          try {
                            await trpc.eventSubscriptions.update.mutate({
                              id,
                              eventType: config.eventType,
                              matchConfig: config,
                            });
                            // Refresh trigger data
                            if (trigger?.id) {
                              await store.dispatch(
                                getTrigger({ id: trigger.id }),
                              );
                            }
                          } catch (error) {
                            console.error("Failed to update trigger:", error);
                            toast.error("Failed to update trigger");
                          }
                        },
                        onEnabledChange: async (id: string, _enabled: boolean) => {
                          try {
                            await store.dispatch(
                              toggleTriggerEventSubscription({ id }),
                            );
                          } catch (error) {
                            console.error("Failed to toggle trigger:", error);
                            toast.error("Failed to toggle trigger");
                          }
                        },
                        onRowClick: (id: string) => {
                          // Find the subscription
                          const sub = triggerSubscriptions.find((s) => s.id === id);
                          if (!sub) return;

                          // Open input mapping dialog (edit existing)
                          store.dispatch(
                            openDialog({
                              schema: "EDIT_TRIGGER_INPUT_MAPPING",
                              params: {
                                mode: "edit",
                                id: sub.id,
                                triggerId: trigger?.id || "",
                                eventType: sub.eventType,
                                matchConfig: sub.matchConfig ?? undefined,
                                inputMappingsMap: sub.inputMappings ?? {},
                              },
                            }),
                          );
                        },
                        onDelete: async (id: string) => {
                          try {
                            await store.dispatch(
                              deleteTriggerEventSubscription({ id }),
                            );
                            toast.success("Trigger deleted");
                          } catch (error) {
                            console.error("Failed to delete trigger:", error);
                            toast.error("Failed to delete trigger");
                          }
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Environment Variables",
              props: {
                header: {
                  title: "Environment Variables",
                  description:
                    "Configure environment variables for this trigger",
                },
                form: {
                  formVersion: "v1",
                  id: "environment-variables-form",
                  fields: (() => {
                    // Get environment variables from workflow schema
                    const workflowSchema = (
                      workflowData as unknown as {
                        schema?: {
                          environment?: Record<
                            string,
                            {
                              required?: boolean;
                              description?: string;
                              label?: string;
                              visibility?: "encrypted" | "visible";
                              editable?: boolean;
                            }
                          >;
                        };
                      }
                    )?.schema;
                    const envDefs = workflowSchema?.environment || {};
                    const envKeys = Object.keys(envDefs);

                    // Get existing values from trigger
                    type EnvVar = {
                      id: string;
                      key: string;
                      label: string;
                      value: string;
                      visibility: "ENCRYPTED" | "VISIBLE";
                      editable: boolean;
                      lastFour?: string | null;
                    };
                    const existingVars: EnvVar[] =
                      (trigger?.envVariables as EnvVar[]) || [];
                    const existingVarsMap = new Map(
                      existingVars.map((v) => [v.key, v] as const),
                    );

                    if (envKeys.length === 0) {
                      return [
                        {
                          component: "Text" as const,
                          span: 12 as const,
                          mdSpan: 12 as const,
                          lgSpan: 12 as const,
                          props: {
                            children:
                              "No environment variables defined in this workflow",
                            variant: "muted" as const,
                          },
                        },
                      ];
                    }

                    return envKeys
                      .map((key) => {
                        const def = envDefs[key];
                        if (!def) return null;

                        const existing = existingVarsMap.get(key);
                        const isEncrypted =
                          def.visibility === "encrypted" ||
                          def.visibility === undefined;
                        const label = def.label || key;
                        const editable = def.editable !== false;

                        // Use SecretInput for encrypted variables, Input for visible ones
                        if (isEncrypted) {
                          // For encrypted variables, check for id or lastFour (since value is empty string)
                          const hasExistingValue =
                            existing?.id &&
                            (existing?.value || existing?.lastFour);

                          return {
                            component: "SecretInput" as const,
                            span: 12 as const,
                            mdSpan: 12 as const,
                            lgSpan: 12 as const,
                            props: {
                              id: `env_${key}`,
                              label,
                              helpText: def.description,
                              value: (existing?.value as string) || "",
                              lastFour: existing?.lastFour || null,
                              required: def.required,
                              placeholder: `Enter ${label}`,
                              disabled: !editable,
                              // Only show View Secret if there's an existing value (id exists and either value or lastFour exists)
                              ...(hasExistingValue && {
                                handleViewSecret: async () => {
                                  if (
                                    !existing?.id ||
                                    !trigger?.id ||
                                    trigger.id === "new"
                                  ) {
                                    return "";
                                  }

                                  try {
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                                    const result = await (
                                      trpc.apps.viewEnvVariable as {
                                        query: (input: {
                                          id: string;
                                        }) => Promise<{
                                          id: string;
                                          key: string;
                                          value: string;
                                          expiresAt: Date | null;
                                        }>;
                                      }
                                    ).query({ id: existing.id });

                                    // If lastFour is null, update it with the last 4 characters of the decrypted value
                                    if (
                                      !existing.lastFour &&
                                      result.value &&
                                      result.value.length >= 4
                                    ) {
                                      try {
                                        // Update the variable to populate lastFour (using the same value to trigger lastFour update)
                                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                                        await (
                                          trpc.workflows
                                            .upsertTriggerEnvVariables as {
                                            mutate: (input: {
                                              triggerId: string;
                                              variables: Array<{
                                                key: string;
                                                label: string;
                                                description: string | null;
                                                value: string;
                                                visibility:
                                                  | "ENCRYPTED"
                                                  | "VISIBLE";
                                                editable: boolean;
                                              }>;
                                            }) => Promise<{ success: boolean }>;
                                          }
                                        ).mutate({
                                          triggerId: trigger.id,
                                          variables: [
                                            {
                                              key,
                                              label,
                                              description:
                                                def.description || null,
                                              value: result.value, // Same value, just to trigger lastFour update
                                              visibility: "ENCRYPTED" as const,
                                              editable,
                                            },
                                          ],
                                        });
                                      } catch (updateError) {
                                        // Silently fail - lastFour update is not critical
                                        console.warn(
                                          "Failed to update lastFour:",
                                          updateError,
                                        );
                                      }
                                    }

                                    return result.value;
                                  } catch (error) {
                                    console.error(
                                      "Failed to view environment variable:",
                                      error,
                                    );
                                    return "";
                                  }
                                },
                              }),
                              handleUpdate: async (value: string) => {
                                if (
                                  !trigger?.id ||
                                  trigger.id === "new" ||
                                  !editable
                                ) {
                                  return;
                                }

                                try {
                                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                                  await (
                                    trpc.workflows
                                      .upsertTriggerEnvVariables as {
                                      mutate: (input: {
                                        triggerId: string;
                                        variables: Array<{
                                          key: string;
                                          label: string;
                                          description: string | null;
                                          value: string;
                                          visibility: "ENCRYPTED" | "VISIBLE";
                                          editable: boolean;
                                        }>;
                                      }) => Promise<{ success: boolean }>;
                                    }
                                  ).mutate({
                                    triggerId: trigger.id,
                                    variables: [
                                      {
                                        key,
                                        label,
                                        description: def.description || null,
                                        value,
                                        visibility: "ENCRYPTED" as const,
                                        editable,
                                      },
                                    ],
                                  });

                                  // Refresh trigger data in Redux to update lastFour
                                  await store.dispatch(
                                    getTrigger({ id: trigger.id }),
                                  );
                                  toast.success(
                                    `${label} updated successfully`,
                                  );
                                } catch (error) {
                                  console.error(
                                    "Failed to update environment variable:",
                                    error,
                                  );
                                  toast.error(`Failed to update ${label}`);
                                }
                              },
                            },
                          };
                        }

                        // Non-encrypted variables use SecretInput with visible mode
                        return {
                          component: "SecretInput" as const,
                          span: 12 as const,
                          mdSpan: 12 as const,
                          lgSpan: 12 as const,
                          props: {
                            id: `env_${key}`,
                            label,
                            helpText: def.description,
                            value: (existing?.value as string) || "",
                            required: def.required,
                            placeholder: `Enter ${label}`,
                            disabled: !editable,
                            visible: true,
                            handleUpdate: async (value: string) => {
                              if (
                                !trigger?.id ||
                                trigger.id === "new" ||
                                !editable
                              ) {
                                return;
                              }

                              try {
                                await store.dispatch(
                                  upsertTriggerEnvVariablesAsync({
                                    triggerId: trigger.id,
                                    variables: [
                                      {
                                        key,
                                        label,
                                        description: def.description || null,
                                        value,
                                        visibility: "VISIBLE" as const,
                                        editable,
                                      },
                                    ],
                                  }),
                                );

                                // Refresh trigger data in Redux to update the displayed value
                                await store.dispatch(
                                  getTrigger({ id: trigger.id }),
                                );
                                toast.success(`${label} updated successfully`);
                              } catch (error) {
                                console.error(
                                  "Failed to save environment variable:",
                                  error,
                                );
                                toast.error(`Failed to update ${label}`);
                              }
                            },
                          },
                        };
                      })
                      .filter(
                        (field): field is NonNullable<typeof field> =>
                          field !== null,
                      );
                  })(),
                },
              },
            },
          ],
        },
      },
    },
  };
};
